import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private profilePictureSubject = new BehaviorSubject<string | null>(localStorage.getItem('picture'));
  private firstNameSubject = new BehaviorSubject<string | null>(localStorage.getItem('firstName'));
  private operationalHoursKey = 'operationalHoursSubmitted';

  setOperationalHoursSubmitted(value: boolean) {
    localStorage.setItem(this.operationalHoursKey, value ? 'true' : 'false');
  }

  getOperationalHoursSubmitted(): boolean {
    return localStorage.getItem(this.operationalHoursKey) === 'true';
  }

  clearOperationalHoursSubmitted() {
    localStorage.removeItem(this.operationalHoursKey);
  }

  setInternationalNumber(num: string) {
    localStorage.setItem('internationalNumber', num);
  }

  setMessageAuditId(id: string) {
    localStorage.setItem('messageAuditId', id);
  }

  setEmail(email: string) {
    localStorage.setItem('email', email);
  }

  setCountryCode(code: string) {
    localStorage.setItem('countryCode', code);
  }

  setNumber(code: string) {
    localStorage.setItem('Number', code);
  }

  setAccesstoken(code: string) {
    localStorage.setItem('accessToken', code);
  }

  setUserEmail(code: string) {
    localStorage.setItem('userEmail', code);
  }

  setForgotPasswordMessageAuditId(code: string) {
    localStorage.setItem('fMessageAuditId', code);
  }

  setForgotEmail(code: string) {
    localStorage.setItem('fEmail', code);
  }

  setModule(code: string) {
    localStorage.setItem('module', code);
  }

  setProfilePicture(picture: string): void {
    localStorage.setItem('picture', picture);
    this.profilePictureSubject.next(picture);
  }

  setFirstName(firstName: string): void {
    localStorage.setItem('firstName', firstName);
    this.firstNameSubject.next(firstName);
  }

  setVerificationStatus(code: string) {
    localStorage.setItem('verificationStatus', code);
  }

  setUserId(code: string) {
    localStorage.setItem('userId', code);
  }

  setIsSubscriptionActive(status: boolean) {
    localStorage.setItem('isSubscriptionActive', JSON.stringify(status));
  }

  setIsSpInvoiceInfoSetUp(status: boolean) {
    localStorage.setItem('isSpInvoiceInfoSetUp', JSON.stringify(status));
  }
  setProfileId(code: string) {
    localStorage.setItem('profileId', code);
  }

  setProfileType(type: string) {
    localStorage.setItem('profileType', type);
  }

  getProfileType(): string | null {
    return localStorage.getItem('profileType');
  }

  getForgotPasswordMessageAuditId(): string | null {
    return localStorage.getItem('fMessageAuditId');
  }

  getProfileId() {
    return localStorage.getItem('profileId');
  }

  getUserId() {
    return localStorage.getItem('userId');
  }

  getForgotEmail(): string | null {
    return localStorage.getItem('fEmail');
  }

  getInternationalNumber(): string | null {
    return localStorage.getItem('internationalNumber');
  }

  setPendingScreen(flag: boolean) {
    localStorage.setItem('pendingScreen', String(flag));
  }

  getPendingScreen(): boolean {
    return localStorage.getItem('pendingScreen') === 'true';
  }

  clearPendingScreen() {
    localStorage.removeItem('pendingScreen');
  }

  getMessageAuditId(): string | null {
    return localStorage.getItem('messageAuditId');
  }

  getEmail(): string | null {
    return localStorage.getItem('email');
  }

  getProfilePictureValue(): string | null {
    return this.profilePictureSubject.value;
  }

  getFirstNameValue(): string | null {
    return this.firstNameSubject.value;
  }

  getProfilePicture$(): Observable<string | null> {
    return this.profilePictureSubject.asObservable();
  }

  getFirstName$(): Observable<string | null> {
    return this.firstNameSubject.asObservable();
  }

  getCountryCode(): string | null {
    return localStorage.getItem('countryCode');
  }

  getNumber(): string | null {
    return localStorage.getItem('Number');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getUserEmail(): string | null {
    return localStorage.getItem('userEmail');
  }

  setStep(step: number): void {
    localStorage.setItem('step', String(step));
  }

  getStep(): number {
    return Number(localStorage.getItem('step') || 1);
  }

  getModule(): string | null {
    return localStorage.getItem('module')
  }

  getVerificationStatus(): string | null {
    return localStorage.getItem('verificationStatus')
  }

  getIsSubscriptionActive(): boolean {
    return JSON.parse(localStorage.getItem('isSubscriptionActive') || 'false');
  }

  getIsSpInvoiceInfoSetUp(): boolean {
    return JSON.parse(localStorage.getItem('isSpInvoiceInfoSetUp') || 'false');
  }

  clearIsSubscriptionActive() {
    localStorage.removeItem('isSubscriptionActive');
  }

  clearIsSpInvoiceInfoSetUp() {
    localStorage.removeItem('isSpInvoiceInfoSetUp');
  }

  clearVerificationStatus() {
    localStorage.removeItem('verificationStatus')
  }

  clearStep() {
    localStorage.removeItem('step');
  }

  clearProfileType() {
    localStorage.removeItem('profileType');
  }

  clearModule() {
    localStorage.removeItem('module');
  }

  clearProfileId() {
    localStorage.removeItem('profileId');
  }

  clearInternationalNumber() {
    localStorage.removeItem('internationalNumber');
  }

  clearMessageAuditId() {
    localStorage.removeItem('messageAuditId');
  }

  clearEmail() {
    localStorage.removeItem('email');
  }

  clearCountryCode() {
    localStorage.removeItem('countryCode');
  }

  clearNumber() {
    localStorage.removeItem('Number');
  }

  clearProfilePicture(): void {
    localStorage.removeItem('picture');
    this.profilePictureSubject.next(null);
  }

  clearFirstName(): void {
    localStorage.removeItem('firstName');
    this.firstNameSubject.next(null);
  }

  clearAccessToken() {
    localStorage.removeItem('accessToken');
  }

  clearUserEmail() {
    localStorage.removeItem('userEmail');
  }

  clearForgotPasswordMessageAuditId() {
    localStorage.removeItem('fMessageAuditId');
  }

  clearForgotEmail() {
    localStorage.removeItem('fEmail');
  }

  clearUserId() {
    localStorage.removeItem('userId');
  }

  setVerificationRemark(type: string) {
    localStorage.setItem('VerificationRemark', type);
  }

  getVerificationRemark(): string | null {
    return localStorage.getItem('VerificationRemark');
  }

  clearVerificationRemark() {
    localStorage.removeItem('VerificationRemark');
  }

  setCurrency(value: string) {
    localStorage.setItem('currency', value);
  }

  getCurrency(): string | null {
    return localStorage.getItem('currency');
  }

  clearCurrency() {
    localStorage.removeItem('currency');
  }

  clearForgotData() {
    this.clearForgotPasswordMessageAuditId();
    this.clearForgotEmail();
  }

  clearAll() {
    this.clearInternationalNumber();
    this.clearMessageAuditId();
    this.clearEmail();
    this.clearCountryCode();
    this.clearNumber();
  }

  clearUserInformation() {
    this.clearAccessToken();
    this.clearProfilePicture();
    this.clearFirstName();
    this.clearVerificationStatus();
    this.clearModule();
    this.clearUserEmail();
    this.clearIsSpInvoiceInfoSetUp();
    this.clearIsSubscriptionActive();
    this.clearUserId();
    this.clearProfileId();
    this.clearStep();
    this.clearProfileType();
    this.clearVerificationRemark();
    this.clearPendingScreen();
    this.clearCurrency();
  }
}
